import {
  FormField,
  FormFieldSection,
  FormFields,
} from 'app/modules/filingFormGenerator/models';
import { GoamlReferenceTables } from 'app/modules/goAML/queries/useGetGoamlFiuConfig';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

type FieldValues = Record<string, any>;

type ReferenceTableKey = keyof GoamlReferenceTables;

interface BaseFieldOpts {
  name: string;
  label: string;
  required?: boolean;
  defaultValue?: string | number | boolean;
  fieldProps?: Record<string, any>;
  isHidden?: boolean;
  help?: string;
}

interface SelectFieldOpts extends BaseFieldOpts {
  options?: { value: string; text: string }[];
  refTable?: ReferenceTableKey;
}

interface RepeatableSectionOpts {
  name: string;
  label: string;
  fields: FormFields<FieldValues>;
  repeatableButtonLabel?: string;
  defaultNumOfEntries?: number;
  isHidden?: boolean;
}

const buildBase = (opts: BaseFieldOpts) => ({
  name: opts.name,
  label: opts.label,
  required: opts.required,
  isHidden: opts.isHidden,
  help: opts.help,
  defaultValue: opts.defaultValue,
  mustExistInPayload: Boolean(opts.required),
  mustHaveValueIfExist: Boolean(opts.required),
  fieldProps: opts.fieldProps,
});

export const textField = (opts: BaseFieldOpts): FormField<FieldValues> => ({
  ...buildBase(opts),
  type: U21FormFieldTypes.TEXT,
});

export const textareaField = (opts: BaseFieldOpts): FormField<FieldValues> => ({
  ...buildBase(opts),
  type: U21FormFieldTypes.TEXTAREA,
});

export const numberField = (opts: BaseFieldOpts): FormField<FieldValues> => ({
  ...buildBase(opts),
  type: U21FormFieldTypes.NUMBER,
});

export const dateField = (opts: BaseFieldOpts): FormField<FieldValues> => ({
  ...buildBase(opts),
  type: U21FormFieldTypes.DATE_PICKER,
});

export const dateTimeField = (opts: BaseFieldOpts): FormField<FieldValues> => ({
  ...buildBase(opts),
  type: U21FormFieldTypes.DATE_TIME_PICKER,
});

export const checkboxField = (opts: BaseFieldOpts): FormField<FieldValues> => ({
  ...buildBase(opts),
  type: U21FormFieldTypes.CHECKBOX,
});

export const selectField = (
  opts: SelectFieldOpts,
  referenceTables?: GoamlReferenceTables,
): FormField<FieldValues> => {
  const options =
    opts.refTable && referenceTables
      ? (referenceTables[opts.refTable] ?? opts.options ?? [])
      : (opts.options ?? []);

  return {
    ...buildBase(opts),
    type: U21FormFieldTypes.SELECT,
    fieldProps: { ...opts.fieldProps, options },
  };
};

export const multiselectField = (
  opts: SelectFieldOpts,
  referenceTables?: GoamlReferenceTables,
): FormField<FieldValues> => {
  const options =
    opts.refTable && referenceTables
      ? (referenceTables[opts.refTable] ?? opts.options ?? [])
      : (opts.options ?? []);

  return {
    ...buildBase(opts),
    type: U21FormFieldTypes.MULTISELECT,
    fieldProps: { ...opts.fieldProps, options },
  };
};

export const repeatableSection = (
  opts: RepeatableSectionOpts,
): FormFieldSection<FieldValues> => ({
  type: 'section',
  name: opts.name,
  label: opts.label,
  isHidden: opts.isHidden,
  repeatable: true,
  repeatableButtonLabel: opts.repeatableButtonLabel ?? opts.label,
  defaultNumOfEntries: opts.defaultNumOfEntries ?? 0,
  mustExistInPayload: false,
  mustHaveValueIfExist: false,
  fields: opts.fields,
});

export const prefixFields = (
  prefix: string,
  fields: FormFields<FieldValues>,
): FormFields<FieldValues> =>
  fields.map((field) => {
    if (Array.isArray(field)) {
      return field.map((f) => ({ ...f, name: `${prefix}.${f.name}` }));
    }
    if (field.type === 'section') {
      return { ...field, name: `${prefix}.${field.name}` };
    }
    return { ...field, name: `${prefix}.${field.name}` };
  });
